// Relative path: src/components/Popup/GalleryPopup.js

import React, { useState, useRef, useEffect } from 'react';
import Popup from './Popup';

/**
 * Gallery popup for displaying clinic images with improved swipe navigation
 * - Responsive design with optimized desktop and mobile experiences
 * - Larger image container on desktop
 * - Full content display with scrolling when needed
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the popup is open
 * @param {Function} props.onClose - Function to call when popup is closed
 * @param {Array} props.images - Array of image objects with downloadURL
 * @param {number} props.initialIndex - Index of initially selected image (optional)
 */
const GalleryPopup = ({ isOpen, onClose, images = [], initialIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(true);
  
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const currentXRef = useRef(0);
  const startTimeRef = useRef(0);
  const carouselRef = useRef(null);
  
  // Detect mobile/desktop
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // 768px is Tailwind's md breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Reset to initial index when images change
  useEffect(() => {
    if (initialIndex >= 0 && initialIndex < images.length) {
      setCurrentIndex(initialIndex);
    } else {
      setCurrentIndex(0);
    }
    setDragOffset(0);
  }, [images, initialIndex]);
  
  // Force proper alignment whenever currentIndex changes
  useEffect(() => {
    if (carouselRef.current) {
      // Reset drag offset
      setDragOffset(0);
      
      // Force proper alignment by accessing the DOM directly
      carouselRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
    }
  }, [currentIndex]);
  
  // Navigate to specific index
  const goToIndex = (index, forceNav = false) => {
    if ((isAnimating && !forceNav) || index === currentIndex || index < 0 || index >= images.length) return;
    
    setIsAnimating(true);
    setCurrentIndex(index);
    setDragOffset(0);
    
    setTimeout(() => {
      setIsAnimating(false);
      
      // Additional alignment check after animation completes
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(-${index * 100}%)`;
      }
    }, 300);
  };
  
  // Go to next image
  const goToNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndex < images.length - 1) {
      goToIndex(currentIndex + 1, true);
    }
  };
  
  // Go to previous image
  const goToPrev = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndex > 0) {
      goToIndex(currentIndex - 1, true);
    }
  };
  
  // Touch event handlers
  const handleTouchStart = (e) => {
    if (images.length <= 1) return;
    
    const touch = e.touches[0];
    startXRef.current = touch.clientX;
    currentXRef.current = touch.clientX;
    startTimeRef.current = Date.now();
    
    setDragOffset(0);
  };
  
  const handleTouchMove = (e) => {
    if (images.length <= 1) return;
    
    const touch = e.touches[0];
    const deltaX = touch.clientX - startXRef.current;
    currentXRef.current = touch.clientX;
    
    // Get container width for percentage calculation
    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
    
    // Calculate as percentage of screen width
    const percentMoved = (deltaX / containerWidth) * 100;
    
    // Apply resistance at edges (first or last image)
    let adjustedPercent = percentMoved;
    
    if ((currentIndex === 0 && percentMoved > 0) || 
        (currentIndex === images.length - 1 && percentMoved < 0)) {
      // Apply resistance - only move 1/3 as far when at the edges
      adjustedPercent = percentMoved / 3;
    }
    
    setDragOffset(adjustedPercent);
  };
  
  const handleTouchEnd = (e) => {
    if (images.length <= 1) return;
    
    // How much we've moved (as a percentage of container width)
    const containerWidth = containerRef.current?.clientWidth || window.innerWidth;
    const deltaX = currentXRef.current - startXRef.current;
    const percentMoved = (deltaX / containerWidth) * 100;
    
    // Calculate swipe velocity
    const timeElapsed = Date.now() - startTimeRef.current;
    const velocity = Math.abs(deltaX) / timeElapsed;
    
    // Threshold for swipe completion - either moved 1/3 of screen width or fast swipe
    const THRESHOLD_PERCENT = 33.3; // One third of screen width
    const VELOCITY_THRESHOLD = 0.5; // Pixels per ms
    
    // Determine if we should navigate or snap back
    if (Math.abs(percentMoved) >= THRESHOLD_PERCENT || velocity > VELOCITY_THRESHOLD) {
      if (percentMoved > 0 && currentIndex > 0) {
        // Swiped right, go to previous
        goToIndex(currentIndex - 1, true);
      } else if (percentMoved < 0 && currentIndex < images.length - 1) {
        // Swiped left, go to next
        goToIndex(currentIndex + 1, true);
      } else {
        // Reset drag offset with animation
        setIsAnimating(true);
        setDragOffset(0);
        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      }
    } else {
      // Reset drag offset with animation
      setIsAnimating(true);
      setDragOffset(0);
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }
  };
  
  // Different content layout for desktop vs mobile
  const renderContent = () => (
    <div className={`py-6 px-4 ${!isMobile ? 'flex flex-col' : ''}`}>
      {/* Main image container */}
      <div 
        ref={containerRef}
        className="relative w-full h-64 md:h-[30rem] bg-gray-100 rounded-lg mb-4 overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={() => {
          // Reset alignment on direct click
          if (carouselRef.current) {
            setDragOffset(0);
            carouselRef.current.style.transform = `translateX(-${currentIndex * 100}%)`;
          }
        }}
      >
        {/* Carousel slider container */}
        <div 
          ref={carouselRef}
          className={`absolute inset-0 flex ${
            isAnimating ? 'transition-transform duration-300 ease-out' : ''
          }`}
          style={{ 
            transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}%))` 
          }}
        >
          {/* Render all images side by side */}
          {images.map((image, index) => (
            <div 
              key={index}
              className="flex-shrink-0 w-full h-full flex items-center justify-center"
            >
              <img 
                src={image.downloadURL} 
                alt={`Clinic gallery image ${index + 1}`}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          ))}
        </div>
        
        {/* Navigation controls */}
        {images.length > 1 && (
          <>
            {/* Left arrow */}
            <button 
              className={`absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 text-white z-10 ${
                currentIndex === 0 ? 'opacity-40 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
              }`}
              onClick={goToPrev}
              disabled={currentIndex === 0}
              aria-label="Previous image"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            {/* Right arrow */}
            <button 
              className={`absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-2 text-white z-10 ${
                currentIndex === images.length - 1 ? 'opacity-40 cursor-not-allowed' : 'opacity-70 hover:opacity-100'
              }`}
              onClick={goToNext}
              disabled={currentIndex === images.length - 1}
              aria-label="Next image"
              type="button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
        
        {/* Image counter indicator */}
        <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 rounded-full px-3 py-1 text-white text-xs">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
      
      {/* Thumbnail grid - Responsive columns based on device */}
      <div className={`grid ${isMobile ? 'grid-cols-4' : 'grid-cols-6 md:grid-cols-8'} gap-2`}>
        {images.map((image, index) => (
          <div 
            key={index} 
            className={`relative cursor-pointer rounded-lg overflow-hidden h-20 transition-all duration-200 ${
              index === currentIndex ? 'ring-4 ring-green-500 scale-[0.95]' : 'hover:opacity-90 active:scale-95'
            }`}
            onClick={() => goToIndex(index)}
          >
            <img 
              src={image.downloadURL} 
              alt={`Clinic gallery thumbnail ${index + 1}`}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
  
  return (
    <Popup 
      isOpen={isOpen} 
      onClose={onClose} 
      className={isMobile ? "max-h-[90vh] flex flex-col" : ""}
    >
      {renderContent()}
    </Popup>
  );
};

export default GalleryPopup;